import { useState, useRef, useEffect } from "react";
import { Crown, X, Loader2, Check } from "lucide-react";
import { ImageCaptureInput } from "@/components/ImageCaptureInput";
import { ImageCropperModal } from "./ImageCropperModal";
import { CategoryIcon } from "./CategoryIcon";
import { InsiderStar } from "@/components/InsiderStar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/authContext";
import { POST_CATEGORIES } from "@shared/schema";
import { getAuthHeader } from "@/lib/authHelpers";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/lib/i18n";

interface PostComposerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCaption?: string;
  wishlistItemId?: string;
  defaultPostAs?: string;
  shopId?: string; // Pre-select a shop
  isShopUpdate?: boolean; // Special mode for shop owners
  initialImages?: string[];
  postId?: string; // For editing
  initialCategory?: string;
  onSuccess?: () => void;
}

interface ImagePreview {
  file: File;
  preview: string;
}

export function PostComposerModal({
  open,
  onOpenChange,
  initialCaption,
  wishlistItemId,
  defaultPostAs,
  shopId: initialShopId,
  isShopUpdate,
  initialImages = [],
  postId,
  initialCategory,
  onSuccess
}: PostComposerModalProps) {
  const { user, tier } = useAuth();
  const { t } = useI18n();
  const [caption, setCaption] = useState(initialCaption || "");
  const [shopId, setShopId] = useState<string>(initialShopId || "");
  const [routeId, setRouteId] = useState<string>("");
  const [category, setCategory] = useState<string>(initialCategory || "");
  const [postAs, setPostAs] = useState<string>(defaultPostAs || "self");
  const [images, setImages] = useState<string[]>(initialImages); // Array of base64 strings
  const [pricePaid, setPricePaid] = useState<string>("");
  // Use locale to set default currency
  const getDefaultCurrency = () => {
    const lang = navigator.language || "nl";
    if (lang.startsWith("en-GB")) return "GBP";
    if (lang.startsWith("en-US") || lang === "en") return "USD";
    if (lang.startsWith("fr") || lang.startsWith("nl") || lang.startsWith("de") || lang.startsWith("es") || lang.startsWith("it") || lang.startsWith("pt")) return "EUR";
    return "EUR";
  };
  const [currency, setCurrency] = useState<string>(getDefaultCurrency());
  const [uploading, setUploading] = useState(false);
  const [shopSearch, setShopSearch] = useState("");
  const [showShopDropdown, setShowShopDropdown] = useState(false);
  const shopSearchRef = useRef<HTMLDivElement>(null);

  // Debounce search input to prevent excessive API calls
  const [debouncedSearch, setDebouncedSearch] = useState(shopSearch);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(shopSearch);
    }, 400);
    return () => clearTimeout(timer);
  }, [shopSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (shopSearchRef.current && !shopSearchRef.current.contains(e.target as Node)) {
        setShowShopDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { data: shops = [], isLoading: loadingShops } = useQuery({
    queryKey: ['shops-search', debouncedSearch],
    queryFn: async () => {
      // If search is empty, don't fetch anything unless we want defaults?
      // Let's allow fetching defaults if search is empty (top shops?)
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("limit", "50");
      const res = await fetch(`/api/shops?${params.toString()}`);
      if (!res.ok) return [];
      const data = await res.json();
      // Handle both plain array and { shops: [] } response
      if (data && typeof data === 'object' && 'shops' in data) {
        return Array.isArray(data.shops) ? data.shops : [];
      }
      return Array.isArray(data) ? data : [];
    },
    enabled: open
  });

  // Fetch initial shop if provided
  const { data: initialShop } = useQuery({
    queryKey: ['shop', initialShopId],
    queryFn: async () => {
      if (!initialShopId) return null;
      const res = await fetch(`/api/shops/${initialShopId}`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: open && !!initialShopId
  });

  const [routes, setRoutes] = useState<Array<{ id: string; name: string }>>([]);
  const [pendingImages, setPendingImages] = useState<string[]>([]);
  const [croppingIndex, setCroppingIndex] = useState(-1);

  // Load routes on mount
  useEffect(() => {
    async function loadData() {
      try {
        // Load routes
        const routesRes = await fetch("/api/routes");
        if (routesRes.ok) {
          const routesData = await routesRes.json();
          setRoutes(routesData.slice(0, 10)); // Limit to 10
        }
      } catch (error) {
        console.error("Error loading data:", error);
      }
    }
    if (open) {
      loadData();
    }
    if (open) {
      if (initialCaption) setCaption(initialCaption);
      if (defaultPostAs) setPostAs(defaultPostAs);
      if (initialShopId) setShopId(initialShopId);
      if (isShopUpdate) setCategory("Winkelnieuws");
      if (initialImages && initialImages.length > 0) setImages(initialImages);
    }
  }, [open, initialCaption, defaultPostAs, initialShopId, isShopUpdate, initialImages]);

  // ... (handleFileSelect, removeImage, handleDrop, handleDragOver, convertToBase64 same as before)


  function removeImage(index: number) {
    setImages(images.filter((_: string, i: number) => i !== index));
  }

  async function handleSubmit() {
    // Validate required fields
    if (!caption.trim()) {
      alert(t("postComposer.captionRequired"));
      return;
    }

    if (!isShopUpdate && images.length === 0) {
      alert(t("postComposer.minOnePhotoRequired"));
      return;
    }

    if (!category) {
      alert(t("postComposer.categoryRequired"));
      return;
    }

    try {
      setUploading(true);

      // Get auth header (async)
      const authHeader = await getAuthHeader();

      if (!authHeader) {
        alert(t("postComposer.loginRequired"));
        setUploading(false);
        return;
      }

      // Upload images (get URLs)
      let imageUrls: string[] = [];
      if (images.length > 0) {
        const uploadRes = await fetch("/api/upload", {
          method: "POST",
          headers: {
            ...authHeader,
          },
          body: JSON.stringify({
            images: images,
            bucket: "posts"
          }),
        });

        if (!uploadRes.ok) {
          const error = await uploadRes.json().catch(() => ({ error: t("postComposer.uploadFailed") }));
          throw new Error(error.error || t("postComposer.uploadFailed"));
        }
        const { urls } = await uploadRes.json();
        imageUrls = urls;
      }

      // Create or update post
      const url = postId ? `/api/posts/${postId}` : "/api/posts";
      const method = postId ? "PATCH" : "POST";

      const postRes = await fetch(url, {
        method,
        headers: {
          ...(authHeader || {}),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          images: imageUrls.length > 0 ? imageUrls : undefined,
          caption: caption.trim(),
          shopId: shopId || undefined,
          routeId: routeId || undefined,
          asShopId: postAs !== "self" ? postAs : undefined,
          linkWishlistItemId: wishlistItemId || undefined,
          category,
          price_paid: pricePaid ? Math.round(parseFloat(pricePaid.replace(',', '.')) * 100) : null,
          currency,
        }),
      });

      if (!postRes.ok) {
        const error = await postRes.json();
        throw new Error(error.error || t("postComposer.createFailed"));
      }

      // Reset form
      setCaption("");
      if (!initialShopId) setShopId("");
      setRouteId("");
      if (!isShopUpdate) setCategory("");
      setPostAs("self");
      setImages([]);

      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error("Error creating post:", error);
      alert(error.message || t("postComposer.genericError"));
    } finally {
      setUploading(false);
    }
  }

  function handleClose() {
    if (!uploading) {
      setCaption("");
      if (!initialShopId) setShopId("");
      setRouteId("");
      if (!isShopUpdate) setCategory("");
      setPostAs("self");
      setImages([]);
      onOpenChange(false);
    }
  }

  const claimedShops = (user?.claimedShopIds || [])
    .map(id => shops.find((s: any) => s.id === id))
    .filter(Boolean) as Array<{ id: string; name: string }>;

  const canSubmit = caption.trim() && category && (isShopUpdate || images.length > 0) && !uploading;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <div className="flex-none p-6 pb-2">
          <DialogTitle>{isShopUpdate ? t("postComposer.placeShopUpdate") : t("postComposer.shareFind")}</DialogTitle>
          <DialogDescription>
            {isShopUpdate
              ? t("postComposer.shopUpdateDesc")
              : t("postComposer.shareDesc")}
          </DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto px-6 overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="space-y-6 pt-2 pb-8">
            {/* Caption */}
            <div className="space-y-2">
              <Label htmlFor="caption">
                {t("postComposer.messageLabel")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Textarea
                id="caption"
                placeholder={isShopUpdate ? t("postComposer.shopUpdatePlaceholder") : t("postComposer.findPlaceholder")}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="resize-none min-h-[100px]"
                rows={4}
                data-testid="input-caption"
              />
            </div>

            {/* Image upload */}
            <div className="space-y-2">
              <Label>
                {t("postComposer.photosLabel")}
                {isShopUpdate ? (
                  <span className="text-muted-foreground ml-1 font-normal">{t("postComposer.optional")}</span>
                ) : (
                  <span className="text-destructive ml-1">*</span>
                )}
              </Label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center bg-muted/5 flex flex-col items-center gap-4">
                <div className="space-y-1 text-muted-foreground">
                  <p className="text-sm font-medium">{t("postComposer.shareFindWithCommunity")}</p>
                  <p className="text-xs">{t("postComposer.maxPhotosHint")}</p>
                </div>

                <ImageCaptureInput
                  multiple
                  onImagesSelected={(files) => {
                    setImages(prev => [...prev, ...files].slice(0, 4));
                  }}
                  label={t("common.upload")}
                />
              </div>



              {/* Image previews */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {images.map((img, index) => (
                    <div key={index} className="relative aspect-square bg-black/5 rounded-lg overflow-hidden border">
                      <img
                        src={(img.startsWith('http') || img.startsWith('/') || img.startsWith('data:')) ? img : `data:image/jpeg;base64,${img}`}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-contain"
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isShopUpdate && (
              <div className="space-y-2">
                <Label>{t("postComposer.shopLabel")}</Label>
                {/* Custom shop search – works reliably inside Dialog on all devices */}
                <div className="relative" ref={shopSearchRef}>
                  {shopId ? (
                    // Show selected shop with clear button
                    <div className="flex items-center gap-2 border rounded-md px-3 py-2 bg-muted/30">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      <span className="flex-1 text-sm truncate">
                        {shops.find((s: any) => s.id === shopId)?.name || initialShop?.name || t("postComposer.selectedShop")}
                        {(shops.find((s: any) => s.id === shopId)?.city || initialShop?.city) && (
                          <span className="text-muted-foreground ml-1">– {shops.find((s: any) => s.id === shopId)?.city || initialShop?.city}</span>
                        )}
                      </span>
                      {!initialShopId && (
                        <button
                          type="button"
                          onClick={() => { setShopId(""); setShopSearch(""); }}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <Input
                      data-testid="select-shop"
                      placeholder={t("postComposer.searchShop")}
                      value={shopSearch}
                      onChange={(e) => {
                        setShopSearch(e.target.value);
                        setShowShopDropdown(true);
                      }}
                      onFocus={() => setShowShopDropdown(true)}
                      disabled={!!initialShopId}
                      autoComplete="off"
                    />
                  )}

                  {/* Results dropdown */}
                  {showShopDropdown && !shopId && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-[300] max-h-52 overflow-y-auto">
                      {loadingShops ? (
                        <div className="py-4 text-center text-sm text-muted-foreground flex justify-center items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t("routeDetail.loading")}
                        </div>
                      ) : shops.length === 0 ? (
                        <div className="py-4 text-center text-sm text-muted-foreground">
                          {t("postComposer.noShopFound")}
                        </div>
                      ) : (
                        shops.map((shop: any) => (
                          <button
                            key={shop.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center gap-2"
                            onMouseDown={(e) => e.preventDefault()} // prevent input blur
                            onClick={() => {
                              setShopId(shop.id);
                              setShopSearch("");
                              setShowShopDropdown(false);
                            }}
                          >
                            <span className="font-medium truncate">{shop.name}</span>
                            {shop.city && <span className="text-muted-foreground shrink-0">– {shop.city}</span>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Added spacer to prevent clipping when dropdown is open */}
            {showShopDropdown && !shopId && <div className="h-40" />}

            <div className="space-y-2">
              <Label>
                {t("postComposer.categoryLabel")}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="select-category">
                  <SelectValue placeholder={t("postComposer.chooseCategory")} />
                </SelectTrigger>
                <SelectContent className="z-[2000]">
                  {POST_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      <span className="flex items-center gap-2">
                        <CategoryIcon category={cat} size={14} />
                        {t(`categories.${cat}`)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Price Paid (Treasures only) */}
            {!isShopUpdate && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pricePaid">{t("community.pricePaid") || "Betaalde prijs"}</Label>
                  <div className="relative">
                    <Input
                      id="pricePaid"
                      type="text"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={pricePaid}
                      onChange={(e) => setPricePaid(e.target.value)}
                      className="pr-12"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                      {currency}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t("community.currency")}</Label>
                  <Select value={currency} onValueChange={setCurrency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[2000]">
                      <SelectItem value="EUR">{t("community.currency.eur")}</SelectItem>
                      <SelectItem value="GBP">{t("community.currency.gbp")}</SelectItem>
                      <SelectItem value="USD">{t("community.currency.usd")}</SelectItem>
                      <SelectItem value="CHF">{t("community.currency.chf")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Visual feedback for shop update */}
            {isShopUpdate && (
              <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/10 rounded-lg text-sm text-primary">
                <Crown className="h-4 w-4 text-yellow-500" />
                {t("postComposer.postOfficiallyAsShop")}
              </div>
            )}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-none p-6 pt-4 border-t bg-background">
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleClose}
              disabled={uploading}
            >
              {t("shops.cancel")}
            </Button>
            <Button
              className="flex-1 shadow-sm"
              onClick={handleSubmit}
              disabled={uploading}
              data-testid="button-submit-post"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("postComposer.sharing")}
                </>
              ) : (
                isShopUpdate ? t("postComposer.placeShopUpdate") : t("postComposer.shareFind")
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
